import { Check } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface WizardStep {
  id: number;
  label: string;
  shortLabel: string;
}

interface WizardProgressProps {
  steps: WizardStep[];
  current: number;
  onJump?: (id: number) => void;
  maxReached: number;
}

export function WizardProgress({ steps, current, onJump, maxReached }: WizardProgressProps) {
  return (
    <div className="sticky top-0 z-20 -mx-2 sm:-mx-4 lg:-mx-6 px-2 sm:px-4 lg:px-6 py-3 bg-background/95 backdrop-blur border-b">
      <div className="flex items-center gap-1 sm:gap-2 overflow-x-auto no-scrollbar">
        {steps.map((step, i) => {
          const isActive = step.id === current;
          const isComplete = step.id < current || (step.id <= maxReached && step.id !== current);
          const reachable = step.id <= maxReached;
          return (
            <div key={step.id} className="flex items-center gap-1 sm:gap-2 shrink-0">
              <button
                type="button"
                onClick={() => reachable && onJump?.(step.id)}
                disabled={!reachable}
                className={cn(
                  'flex items-center gap-2 rounded-full px-2 sm:px-3 py-1.5 transition-colors',
                  isActive && 'bg-primary text-primary-foreground',
                  !isActive && isComplete && 'text-primary hover:bg-primary/10',
                  !isActive && !isComplete && 'text-muted-foreground',
                  reachable && !isActive && 'cursor-pointer',
                  !reachable && 'cursor-not-allowed opacity-60',
                )}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full border text-xs font-bold shrink-0',
                    isActive && 'border-primary-foreground bg-primary-foreground text-primary',
                    !isActive && isComplete && 'border-primary bg-primary text-primary-foreground',
                    !isActive && !isComplete && 'border-muted-foreground/40',
                  )}
                >
                  {isComplete && !isActive ? <Check className="h-3.5 w-3.5" /> : step.id}
                </span>
                <span className="text-xs sm:text-sm font-medium whitespace-nowrap hidden sm:inline">{step.label}</span>
                <span className="text-xs font-medium whitespace-nowrap sm:hidden">{step.shortLabel}</span>
              </button>
              {i < steps.length - 1 && <span className="h-px w-3 sm:w-6 bg-border shrink-0" />}
            </div>
          );
        })}
      </div>
    </div>
  );
}
